import { apiSlice } from '../apiSlice';
import { Paginated, SuccessResponse } from '../DTO/common.interface';
import {
	InterventionDTO,
	InterventionRequest,
} from '../DTO/member-management/intervention';
import {
	LogHistoryDTO,
	LogHistoryFilter,
} from '../DTO/pre-admission/preAdmission.interface';

export const intervention = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getInterventionDetail: builder.query<
			SuccessResponse<InterventionDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/intervention/${id}`,
				method: 'GET',
			}),

			providesTags: ['InterventionDetail'],
		}),
		getInterventionHistory: builder.query<
			SuccessResponse<Paginated<LogHistoryDTO>>,
			LogHistoryFilter
		>({
			query: (body) => ({
				url: `/web/intervention/${body.formId}/history-page`,
				method: 'POST',
				body,
			}),
			providesTags: ['InterventionHistory'],
		}),
		// `data` is true when the intervention promoted the member from
		// "Pending intake" to "Intake completed" (nature code 5-7) and the member
		// is still in that status, i.e. deleting it can revert the member status.
		checkInterventionRevertStatus: builder.query<
			SuccessResponse<boolean>,
			string
		>({
			query: (id) => ({
				url: `/web/intervention/${id}/check-revert-status`,
				method: 'GET',
			}),
		}),
		// Editing the nature can move the member's status with it. `data` says
		// which question to ask before saving: 0 = none, 1 = the nature left code
		// 5-7 so the member can go back to "Pending intake", 2 = the nature entered
		// code 5-7 so the member can move to "Intake completed".
		checkInterventionChangeStatus: builder.query<
			SuccessResponse<number>,
			{ id: string; nature: string }
		>({
			query: ({ id, nature }) => ({
				url: `/web/intervention/${id}/check-change-status`,
				method: 'GET',
				params: { nature },
			}),
		}),
		deleteIntervention: builder.mutation<
			SuccessResponse<any>,
			{ id: string; revertStatus?: boolean }
		>({
			query: ({ id, revertStatus }) => ({
				url: `/web/intervention/${id}`,
				method: 'DELETE',
				params: { revert_status: revertStatus ?? false },
			}),
			// The member status may have been reverted, so refresh member views.
			invalidatesTags: ['AdmissionRequestDetail', 'MyInterventionList'],
		}),
		draftIntervention: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/intervention/${id}/change-status`,
				method: 'PUT',
			}),
			invalidatesTags: ['InterventionDetail', 'InterventionHistory'],
		}),
		getMemberTree: builder.query<SuccessResponse<any>, null>({
			query: () => ({
				url: `/web/intervention/as-different-worker`,
				method: 'GET',
			}),
		}),
		createIntervention: builder.mutation<SuccessResponse<any>, FormData>({
			query: (body) => ({
				url: `/web/intervention`,
				method: 'POST',
				body,
			}),
		}),
		checkIntervention: builder.mutation<SuccessResponse<any>, FormData>({
			query: (body) => ({
				url: `/web/intervention/check-time-conflict`,
				method: 'POST',
				body,
			}),
		}),
		editIntervention: builder.mutation<
			SuccessResponse<any>,
			{ body: any; changeStatus?: boolean }
		>({
			query: ({ body, changeStatus }) => ({
				url: `/web/intervention/${body.id}/detail`,
				method: 'PUT',
				body,
				params: { change_status: changeStatus ?? false },
			}),
			// The member status may have moved with the nature, so refresh member views.
			invalidatesTags: ['AdmissionRequestDetail', 'MyInterventionList'],
		}),
		editCaseNote: builder.mutation<
			SuccessResponse<any>,
			{ formData: FormData; id: string }
		>({
			query: (body) => ({
				url: `/web/intervention/${body.id}/case-note`,
				method: 'PUT',
				body: body.formData,
			}),

			invalidatesTags: ['MyInterventionList'],
		}),
	}),
});

export const {
	useGetInterventionDetailQuery,
	useLazyCheckInterventionRevertStatusQuery,
	useLazyCheckInterventionChangeStatusQuery,
	useDeleteInterventionMutation,
	useDraftInterventionMutation,
	useGetInterventionHistoryQuery,
	useGetMemberTreeQuery,
	useCreateInterventionMutation,
	useCheckInterventionMutation,
	useEditInterventionMutation,
	useEditCaseNoteMutation,
} = intervention;
